<script setup lang="ts">
import { ref, computed, useTemplateRef } from 'vue';
import { z } from 'zod';
import { UiForm, UiFormField, UiInputField, UiText, UiButton, UiCheckbox } from '@shopexperts/shared';
import type { ShopifyPlan } from '@shopexperts/shared';
import LeadStepHeader from './LeadStepHeader.vue';
import SelectableSlideCard from './SelectableSlideCard.vue';

interface Store {
  id: number;
  title: string;
  url: string;
}

interface Props {
  currentStep: number;
  totalSteps: number;
  isAuthenticated: boolean;
  stores?: Store[];
  isLoadingStores?: boolean;
  selectedExpertName?: string;
}

const props = withDefaults(defineProps<Props>(), {
  stores: () => [],
  isLoadingStores: false,
  selectedExpertName: undefined,
});

const copy = computed(() => {
  const name = props.selectedExpertName;
  if (name) {
    return {
      subheading: `Share your Shopify store details so ${name} can get started on your project.`,
      newStore: `${name} can help you set up a new Shopify store.`,
      existingStore: `Enter your store URL so ${name} can review it.`,
      firstStore: `${name} can help you set up your first Shopify store.`,
    };
  }
  return {
    subheading: 'We need some information about your Shopify store to match you with the right expert.',
    newStore: 'Connect with an expert who can help you set up a new Shopify store.',
    existingStore: 'Enter your store URL to connect with experts who can help.',
    firstStore: 'Get matched with an expert who can help you set up your first Shopify store.',
  };
});

const emit = defineEmits<{
  (e: 'next', data: StoreInformationData): void;
  (e: 'back'): void;
}>();

export interface StoreInformationData {
  storeMode: 'existing' | 'new';
  storeId?: number;
  storeUrl?: string;
  storeName?: string;
  storePlan?: ShopifyPlan;
}

type StoreChoice = 'none' | 'existing' | 'new' | 'manual' | number;
const storeChoice = ref<StoreChoice>('none');
const selectionError = ref<string | undefined>(undefined);

const selectedStoreId = ref<number | undefined>(undefined);

const manualStoreSubmitRef = useTemplateRef<HTMLButtonElement>('manualStoreSubmitRef');

const manualStoreSchema = z.object({
  storeUrl: z.preprocess(
    (val) => val === undefined || val === null ? '' : val,
    z.string()
      .min(1, 'Store URL is required')
      .max(500, 'Store URL must be 500 characters or less')
  ),
  storeName: z.preprocess(
    (val) => val === undefined || val === null ? '' : val,
    z.string()
      .min(1, 'Store name is required')
      .max(255, 'Store name must be 255 characters or less')
  ),
  isPlusStore: z.boolean().optional(),
});

const manualStoreModel = ref({
  storeUrl: '',
  storeName: '',
  isPlusStore: false,
});

const showStoreSelector = computed(() => {
  return props.isAuthenticated && !props.isLoadingStores && props.stores && props.stores.length > 0;
});

const showNoStoresMessage = computed(() => {
  return props.isAuthenticated && !props.isLoadingStores && (!props.stores || props.stores.length === 0);
});

const showModeSelection = computed(() => {
  return !props.isAuthenticated;
});

const isExistingSelected = computed<boolean>({
  get: () => storeChoice.value === 'existing',
  set: (val) => {
    if (val) storeChoice.value = 'existing';
    else if (storeChoice.value === 'existing') storeChoice.value = 'none';
  },
});

const isManualSelected = computed<boolean>({
  get: () => storeChoice.value === 'manual',
  set: (val) => {
    if (val) storeChoice.value = 'manual';
    else if (storeChoice.value === 'manual') storeChoice.value = 'none';
  },
});

const isNewSelected = computed<boolean>({
  get: () => storeChoice.value === 'new',
  set: (val) => {
    if (val) storeChoice.value = 'new';
    else if (storeChoice.value === 'new') storeChoice.value = 'none';
  },
});

function isStoreSelected(storeId: number) {
  return computed<boolean>({
    get: () => storeChoice.value === storeId,
    set: (val) => {
      if (val) {
        storeChoice.value = storeId;
        selectedStoreId.value = storeId;
      } else if (storeChoice.value === storeId) {
        storeChoice.value = 'none';
        selectedStoreId.value = undefined;
      }
    },
  });
}

function onSubmitManualStore(values: { storeUrl: string; storeName: string; isPlusStore?: boolean }) {
  emit('next', {
    storeMode: 'existing',
    storeUrl: values.storeUrl,
    storeName: values.storeName,
    storePlan: values.isPlusStore ? 'plus' : 'basic',
  });
}

function handleContinue() {
  selectionError.value = undefined;
  
  if (storeChoice.value === 'none') {
    selectionError.value = 'Please select an option to continue.';
    return;
  }
  
  if (storeChoice.value === 'new') {
    emit('next', {
      storeMode: 'new',
      storeUrl: undefined,
      storeName: undefined,
      storePlan: 'basic',
    });
  } else if (storeChoice.value === 'existing' || storeChoice.value === 'manual') {
    manualStoreSubmitRef.value?.click();
  } else if (typeof storeChoice.value === 'number') {
    // Selected an existing store from the list
    emit('next', {
      storeMode: 'existing',
      storeId: storeChoice.value,
      storePlan: 'basic',
    });
  }
}

function handleBack() {
  emit('back');
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <LeadStepHeader
      caption="Store Information"
      heading="Tell us about your store"
      :subheading="copy.subheading"
      :current-step="currentStep"
      :total-steps="totalSteps"
    >
      <template #close-button>
        <slot name="close-button" />
      </template>
    </LeadStepHeader>

    <!-- Authenticated: Store Selector -->
    <template v-if="showStoreSelector">
      <div class="flex flex-col gap-5">
        <UiText as="p" variant="p" class="text-se-gray-600">
          Select one of your stores or add a new one
        </UiText>

        <div class="flex flex-col gap-3">
          <!-- Existing stores -->
          <SelectableSlideCard
            v-for="store in stores"
            :key="store.id"
            v-model="isStoreSelected(store.id).value"
            :title="store.title"
            :description="store.url"
            :allow-deselect="true"
            :expandable="false"
          />

          <!-- Add an existing store -->
          <SelectableSlideCard
            v-model="isManualSelected"
            title="Add an existing store"
            description="Enter your store URL to add it to your account."
            :allow-deselect="true"
            :expandable="true"
          >
            <UiForm
              v-model="manualStoreModel"
              :schema="manualStoreSchema"
              @submit="onSubmitManualStore"
            >
              <div class="flex flex-col gap-4">
                <button ref="manualStoreSubmitRef" type="submit" class="hidden" />

                <UiFormField name="storeUrl" label="Store URL" type="url">
                  <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiInputField
                      :id="id"
                      name="storeUrl"
                      type="text"
                      :model-value="modelValue as string"
                      @update:model-value="onUpdateModelValue"
                      @validate="onValidate"
                      :invalid="invalid"
                      :error="error"
                      placeholder="https://mystore.com"
                    />
                  </template>
                </UiFormField>

                <UiFormField name="storeName" label="Store Name" type="text">
                  <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiInputField
                      :id="id"
                      name="storeName"
                      :model-value="modelValue as string"
                      @update:model-value="onUpdateModelValue"
                      @validate="onValidate"
                      :invalid="invalid"
                      :error="error"
                      placeholder="My Awesome Store"
                    />
                  </template>
                </UiFormField>

                <UiFormField name="isPlusStore" type="text">
                  <template #default="{ modelValue, onUpdateModelValue, id, disabled }">
                    <div class="flex items-center gap-3">
                      <UiCheckbox
                        :id="id"
                        :model-value="Boolean(modelValue)"
                        @update:model-value="onUpdateModelValue"
                        :disabled="disabled"
                        label="This store is on the Shopify Plus plan"
                      />
                    </div>
                  </template>
                </UiFormField>
              </div>
            </UiForm>
          </SelectableSlideCard>

          <!-- Get help creating a new store -->
          <SelectableSlideCard
            v-model="isNewSelected"
            title="Get help creating a new store"
            :description="copy.newStore"
            :allow-deselect="true"
            :expandable="false"
          />
        </div>

        <UiText 
          v-if="selectionError" 
          as="p" 
          variant="p-small" 
          class="text-red-600"
          :text="selectionError"
        />

        <div class="flex items-center gap-4 pt-6">
          <UiButton type="button" variant="outline" label="Back" @click="emit('back')" />
          <UiButton type="button" variant="primary" label="Continue" class="flex-1" @click="handleContinue" />
        </div>
      </div>
    </template>

    <!-- Authenticated: No stores yet -->
    <template v-else-if="showNoStoresMessage">
      <div class="flex flex-col gap-5">
        <div class="rounded-md border border-se-gray-200 bg-se-gray-50 p-4">
          <UiText as="p" variant="p-small" class="text-se-gray-700">
            You don't have any stores yet. Add an existing store or get help creating a new one.
          </UiText>
        </div>

        <SelectableSlideCard
          v-model="isExistingSelected"
          title="Add an existing store"
          description="Enter your store URL to add it to your account."
          :allow-deselect="true"
          :expandable="true"
        >
          <UiForm
            v-model="manualStoreModel"
            :schema="manualStoreSchema"
            @submit="onSubmitManualStore"
          >
            <div class="flex flex-col gap-4">
              <button ref="manualStoreSubmitRef" type="submit" class="hidden" />

              <UiFormField name="storeUrl" label="Store URL" type="url">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                  <UiInputField
                    :id="id"
                    name="storeUrl"
                    type="text"
                    :model-value="modelValue as string"
                    @update:model-value="onUpdateModelValue"
                    @validate="onValidate"
                    :invalid="invalid"
                    :error="error"
                    placeholder="https://your-store.myshopify.com"
                  />
                </template>
              </UiFormField>

              <UiFormField name="storeName" label="Store Name" type="text">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                  <UiInputField
                    :id="id"
                    name="storeName"
                    :model-value="modelValue as string"
                    @update:model-value="onUpdateModelValue"
                    @validate="onValidate"
                    :invalid="invalid"
                    :error="error"
                    placeholder="My Awesome Store"
                  />
                </template>
              </UiFormField>

              <UiFormField name="isPlusStore" type="text">
                <template #default="{ modelValue, onUpdateModelValue, id, disabled }">
                  <div class="flex items-center gap-3">
                    <UiCheckbox
                      :id="id"
                      :model-value="Boolean(modelValue)"
                      @update:model-value="onUpdateModelValue"
                      :disabled="disabled"
                      label="This store is on the Shopify Plus plan"
                    />
                  </div>
                </template>
              </UiFormField>
            </div>
          </UiForm>
        </SelectableSlideCard>

        <SelectableSlideCard
          v-model="isNewSelected"
          title="Get help creating a new store"
          :description="copy.newStore"
          :expandable="false"
          :allow-deselect="true"
        />

        <UiText 
          v-if="selectionError" 
          as="p" 
          variant="p-small" 
          class="text-red-600"
          :text="selectionError"
        />

        <div class="flex items-center gap-4 pt-6">
          <UiButton type="button" variant="outline" label="Back" @click="handleBack" />
          <UiButton type="button" variant="primary" label="Continue" class="flex-1" @click="handleContinue" />
        </div>
      </div>
    </template>

    <!-- Unauthenticated: Mode Selection with Inline Form -->
    <template v-else-if="showModeSelection">
      <div class="flex flex-col gap-4">
        <UiText as="p" variant="p" class="text-se-gray-600">
          Do you already have a Shopify store?
        </UiText>
        
        <div class="flex flex-col gap-3">
          <!-- I have a store (expandable) -->
          <SelectableSlideCard
            v-model="isExistingSelected"
            title="Yes, I have a Shopify store"
            :description="copy.existingStore"
            :allow-deselect="true"
            :expandable="true"
          >
            <UiForm
              v-model="manualStoreModel"
              :schema="manualStoreSchema"
              @submit="onSubmitManualStore"
            >
              <div class="flex flex-col gap-4">
                <!-- Hidden submit button for programmatic submission -->
                <button ref="manualStoreSubmitRef" type="submit" class="hidden" />

                <UiFormField
                  name="storeUrl"
                  label="Store URL"
                  type="url"
                >
                  <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiInputField
                      :id="id"
                      name="storeUrl"
                      type="text"
                      :model-value="modelValue as string"
                      @update:model-value="onUpdateModelValue"
                      @validate="onValidate"
                      :invalid="invalid"
                      :error="error"
                      placeholder="https://mystore.com"
                    />
                  </template>
                </UiFormField>

                <UiFormField
                  name="storeName"
                  label="Store Name"
                  type="text"
                >
                  <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiInputField
                      :id="id"
                      name="storeName"
                      :model-value="modelValue as string"
                      @update:model-value="onUpdateModelValue"
                      @validate="onValidate"
                      :invalid="invalid"
                      :error="error"
                      placeholder="My Awesome Store"
                    />
                  </template>
                </UiFormField>

                <UiFormField name="isPlusStore" type="text">
                  <template #default="{ modelValue, onUpdateModelValue, id, disabled }">
                    <div class="flex items-center gap-3">
                      <UiCheckbox
                        :id="id"
                        :model-value="Boolean(modelValue)"
                        @update:model-value="onUpdateModelValue"
                        :disabled="disabled"
                        label="This store is on the Shopify Plus plan"
                      />
                    </div>
                  </template>
                </UiFormField>


              </div>
            </UiForm>
          </SelectableSlideCard>

          <!-- Create new store (select-only) -->
          <SelectableSlideCard
            v-model="isNewSelected"
            title="No, I need to create a new store"
            :description="copy.firstStore"
            :expandable="false"
            :allow-deselect="true"
          />
        </div>

        <!-- Selection error -->
        <UiText 
          v-if="selectionError" 
          as="p" 
          variant="p-small" 
          class="text-red-600"
          :text="selectionError"
        />

        <!-- Actions -->
        <div class="flex items-center gap-4 pt-6">
          <UiButton
            type="button"
            variant="outline"
            label="Back"
            @click="handleBack"
          />
          <UiButton
            type="button"
            variant="primary"
            label="Continue"
            class="flex-1"
            @click="handleContinue"
          />
        </div>
      </div>
    </template>
  </div>
</template>
